import { useAuth } from "@clerk/react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import type { NotificationListItem } from "../../../shared/notification-list";
import { authenticatedApi } from "@/lib/api";
import {
  countAfterRead,
  createNotificationsClient,
  nextNotificationCursor,
  NOTIFICATION_QUERY_KEYS,
  UNREAD_COUNT_POLL_MS,
  withItemRead,
  type NotificationPages,
  type NotificationsClient,
} from "@/lib/notifications-client";

// The in-app notification queries (NO-830). The list carries Client names, so
// its query key holds nothing but a constant; the count is a number.

function useNotificationsClient(): NotificationsClient {
  const { getToken } = useAuth();

  return useMemo(
    () =>
      createNotificationsClient(<T>(path: string, init?: RequestInit) =>
        authenticatedApi<T>(getToken, path, init)
      ),
    [getToken]
  );
}

/** The rail's unread count, polled only while `enabled`; 0 otherwise. */
export function useUnreadNotificationCount(enabled: boolean): number {
  const client = useNotificationsClient();
  const query = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount,
    enabled,
    refetchInterval: enabled ? UNREAD_COUNT_POLL_MS : false,
    queryFn: () => client.unreadCount(),
  });

  return enabled ? (query.data ?? 0) : 0;
}

/** The list, one page at a time: each older page follows the last one's cursor. */
export function useNotificationList() {
  const client = useNotificationsClient();

  return useInfiniteQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.list,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => client.list(pageParam),
    getNextPageParam: nextNotificationCursor,
  });
}

/**
 * Marks an item read: the list and the rail count change at once, and both
 * refetch when the request settles. The callbacks live on the mutation, so
 * they still run after the page navigates to the Appointment.
 */
export function useMarkNotificationRead() {
  const client = useNotificationsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item: NotificationListItem) => client.markRead(item.id),
    onMutate: async (item) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      queryClient.setQueryData<NotificationPages>(
        NOTIFICATION_QUERY_KEYS.list,
        (data) => withItemRead(data, item.id, new Date().toISOString())
      );
      queryClient.setQueryData<number>(NOTIFICATION_QUERY_KEYS.unreadCount, (count) =>
        countAfterRead(count, item)
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
}
